/**
 * ScheduleDrawer - Bottom collapsible schedule drawer floating over the globe.
 *
 * Part of the designer's trip-detail UI layout.
 * Position: fixed, bottom
 */

import { useMemo, useState } from 'react'
import { ChevronUp, Clock, MapPin } from 'lucide-react'
import { cn } from '@/lib/style'
import { formatTime } from '@/lib/date'
import { useScheduleFirestore, useEventsFirestore } from '@/hooks/schedule'

import type { Event as EventMessage, Trip as TripMessage } from '@generated/proto/model'

interface ScheduleDrawerProps {
  trip: TripMessage
  tripId: string
  onToggle?: (isOpen: boolean) => void
}

function EventPill({ event, time }: { event: EventMessage; time: string }) {
  return (
    <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5 flex-shrink-0">
      <span
        className="text-white/60 text-sm"
        style={{ fontFamily: "var(--font-alga), sans-serif" }}
      >
        {time}
      </span>
      <span
        className="text-white text-sm font-medium truncate max-w-[150px]"
        style={{ fontFamily: "var(--font-alga), sans-serif" }}
      >
        {event.title}
      </span>
    </div>
  )
}

function EventCard({ event, onClick }: { event: EventMessage; onClick?: () => void }) {
  const imageUrl = event.images?.images?.[0]?.url || '/images/event-placeholder.jpg'
  const timeDisplay = formatTime(event.startTime)
  const placeName = event.place?.name || event.place?.location

  return (
    <div
      onClick={onClick}
      className={cn(
        "flex-shrink-0 w-[220px] cursor-pointer",
        "bg-black/50 backdrop-blur-md rounded-2xl overflow-hidden",
        "border border-white/20 hover:border-white/40",
        "transition-all duration-300 hover:scale-105"
      )}
    >
      {/* Image */}
      <div className="relative h-28 w-full overflow-hidden">
        <img
          src={imageUrl}
          alt={event.title || 'Event'}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.src = '/images/event-placeholder.jpg'
          }}
        />
        {/* Time Badge */}
        <div className="absolute top-2 left-2 bg-black/70 backdrop-blur-sm rounded-full px-2 py-0.5 flex items-center gap-1">
          <Clock className="w-3 h-3 text-white" />
          <span
            className="text-sm text-white"
            style={{ fontFamily: "var(--font-alga), sans-serif" }}
          >
            {timeDisplay}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Title */}
        <h3
          className="text-white font-bold text-base mb-1 line-clamp-1"
          style={{ fontFamily: "'Pioneer Boulevard', sans-serif" }}
        >
          {event.title || 'Untitled Event'}
        </h3>

        {/* Description */}
        {event.description && (
          <p
            className="text-white/70 text-sm mb-2 line-clamp-2"
            style={{ fontFamily: "var(--font-alga), sans-serif" }}
          >
            {event.description}
          </p>
        )}

        {/* Location */}
        {placeName && (
          <div className="flex items-center gap-1 text-white/60">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span
              className="text-sm truncate"
              style={{ fontFamily: "var(--font-alga), sans-serif" }}
            >
              {placeName}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export function ScheduleDrawer({ trip, tripId, onToggle }: ScheduleDrawerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const { schedules } = useScheduleFirestore(tripId)
  const firstSchedule = schedules?.[0]
  const scheduleId = firstSchedule?.proto?.scheduleId
  const { events: firestoreEvents } = useEventsFirestore(tripId, scheduleId || '')

  const events = useMemo(() => {
    if (firestoreEvents && firestoreEvents.length > 0) {
      return firestoreEvents.map((e) => e.proto)
    }
    return []
  }, [firestoreEvents])

  const handleToggle = () => {
    const newState = !isOpen
    setIsOpen(newState)
    onToggle?.(newState)
  }

  const eventCount = events.length

  return (
    <>
      {/* Minimized View - Shows event titles */}
      <div
        className={cn(
          "fixed bottom-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out pointer-events-auto",
          "bg-black/50 backdrop-blur-md border-t border-white/20 rounded-t-2xl",
          isOpen ? "opacity-0 pointer-events-none translate-y-full" : "opacity-100"
        )}
      >
        {/* Header Row */}
        <button
          onClick={handleToggle}
          className="w-full flex items-center justify-between px-4 py-2 text-left"
        >
          <div className="flex items-center gap-3">
            <h2
              className="text-white font-bold text-base"
              style={{ fontFamily: "'Pioneer Boulevard', sans-serif" }}
            >
              {trip.title || 'Schedule'}
            </h2>
            <span
              className="text-white/50 text-sm"
              style={{ fontFamily: "var(--font-alga), sans-serif" }}
            >
              {eventCount} events
            </span>
          </div>
          <ChevronUp className="w-4 h-4 text-white/60" />
        </button>

        {/* Event Pills Row */}
        {events.length > 0 && (
          <div
            className="flex gap-2 px-4 pb-3 overflow-x-auto"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {events.map((event, index) => (
              <EventPill
                key={event.eventId || `event-pill-${index}`}
                event={event}
                time={formatTime(event.startTime)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Expanded View */}
      <div
        className={cn(
          "fixed bottom-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out pointer-events-auto",
          "bg-black/50 backdrop-blur-md border-t border-white/20 rounded-t-2xl",
          isOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-full pointer-events-none"
        )}
        style={{ height: '50vh' }}
      >
        {/* Header */}
        <button
          onClick={handleToggle}
          className="w-full flex items-center justify-between px-6 py-4 text-left border-b border-white/10"
        >
          <div>
            <h2
              className="text-white font-bold text-xl"
              style={{ fontFamily: "'Pioneer Boulevard', sans-serif" }}
            >
              {trip.title || 'Your Trip'}
            </h2>
            <p
              className="text-white/60 text-sm"
              style={{ fontFamily: "var(--font-alga), sans-serif" }}
            >
              {eventCount} {eventCount === 1 ? 'event' : 'events'} scheduled
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span
              className="text-white/60 text-base"
              style={{ fontFamily: "var(--font-alga), sans-serif" }}
            >
              Hide
            </span>
            <ChevronUp className="w-5 h-5 text-white/60 rotate-180" />
          </div>
        </button>

        {/* Events Carousel */}
        <div className="h-[calc(100%-72px)] px-6 py-4 overflow-hidden">
          {events.length > 0 ? (
            <div
              className="flex gap-4 overflow-x-auto h-full items-start pb-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {events.map((event, index) => (
                <div key={event.eventId || `event-card-${index}`} className="flex flex-col gap-2 flex-shrink-0">
                  {/* Day indicator - shows order, real dates require Day entity lookup */}
                  <div className="text-center">
                    <div
                      className="text-sm text-white/60 uppercase tracking-wider"
                      style={{ fontFamily: "var(--font-alga), sans-serif" }}
                    >
                      Day {index + 1}
                    </div>
                  </div>
                  <EventCard event={event} />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p
                className="text-white/50 text-base"
                style={{ fontFamily: "var(--font-alga), sans-serif" }}
              >
                No events scheduled yet. Chat with Carmen to plan your trip!
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
